import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { CalendarDays, ClipboardCheck, ReceiptText, Bot, LogOut, MailPlus, CheckCircle, AlertTriangle, Clock, Users, Plus, X } from 'lucide-react';
import PageHeader from './PageHeader';

import { API } from '../apiClient';
function api(path, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? {Authorization: `Bearer ${token}`} : {}),
      ...(options.headers || {})
    }
  }).then(async r => {
    if (!r.ok) throw new Error((await r.json()).detail || 'API error');
    return r.json();
  });
}

function CleaningStatusChip({cleaning_job_id, cleaning_status, cleaning_scheduled_date, booking_checkin, booking_checkout}) {
  const labels = {
    done: 'Posprzątane',
    planned: 'Nieposprzątane',
    in_progress: 'W trakcie sprzątania',
    missing: 'Brak zadania sprzątania'
  };
  // If there's no cleaning job at all, show neutral gray chip.
  if (!cleaning_job_id) {
    return (
      <span className="cleaning-status-chip cleaning-status-missing" style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'4px 9px',borderRadius:'999px',fontSize:'11px',fontWeight:'700'}}>
        ⚪ Brak zadania sprzątania
      </span>
    );
  }
  const today = todayIso();
  // Context-aware status: only show "Nieposprzątane" (red) when the job is scheduled
  // for today or earlier AND it isn't done. Future-scheduled jobs (typically the
  // post-checkout cleaning) get a neutral "scheduled" chip.
  const isDone = cleaning_status === 'done';
  const isInProgress = cleaning_status === 'in_progress';
  if (isDone) {
    return (
      <span className="cleaning-status-chip cleaning-status-done" style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'4px 9px',borderRadius:'999px',fontSize:'11px',fontWeight:'700'}}>
        🟢 Posprzątane
      </span>
    );
  }
  if (isInProgress) {
    return (
      <span className="cleaning-status-chip cleaning-status-in-progress" style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'4px 9px',borderRadius:'999px',fontSize:'11px',fontWeight:'700'}}>
        🟠 W trakcie sprzątania
      </span>
    );
  }
  // cleaning_status === 'planned' (or anything not done/in_progress)
  if (cleaning_scheduled_date && cleaning_scheduled_date > today) {
    // Future-scheduled — e.g. cleaning after a future checkout.
    // Show neutral chip with scheduled date, not red.
    const showDate = cleaning_scheduled_date.slice(5); // MM-DD
    return (
      <span className="cleaning-status-chip cleaning-status-future" style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'4px 9px',borderRadius:'999px',fontSize:'11px',fontWeight:'700'}}>
        ⚪ Sprzątanie zaplanowane na {showDate}
      </span>
    );
  }
  if (cleaning_scheduled_date && cleaning_scheduled_date <= today) {
    // Scheduled today or earlier and not done — truly "needs cleaning now".
    return (
      <span className="cleaning-status-chip cleaning-status-planned" style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'4px 9px',borderRadius:'999px',fontSize:'11px',fontWeight:'700'}}>
        🔴 Nieposprzątane
      </span>
    );
  }
  // Fallback: has job, has no date (shouldn't happen, but be safe).
  return (
    <span className="cleaning-status-chip cleaning-status-planned" style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'4px 9px',borderRadius:'999px',fontSize:'11px',fontWeight:'700'}}>
      🔴 {labels[cleaning_status] || 'Nieposprzątane'}
    </span>
  );
}

function todayIso() {
  return new Date().toISOString().split('T')[0];
}

function reservationNumber(externalId, id) {
  if (externalId) {
    const value = String(externalId);
    return value.startsWith('booking:') ? value.slice('booking:'.length) : value;
  }
  return id ? String(id) : '—';
}


function formatWarsawDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(value).replace('T', ' ').slice(0, 16);
  }
}

function formatLag(seconds) {
  if (seconds === null || seconds === undefined || seconds === '') return '—';
  const total = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m <= 0) return `${s} s`;
  return `${m} min ${s} s`;
}

function importLagTone(seconds) {
  if (seconds === null || seconds === undefined || seconds === '') return '#64748b';
  const n = Number(seconds) || 0;
  if (n < 300) return '#16a34a';
  if (n < 1800) return '#d97706';
  return '#dc2626';
}

function importPathLabel(path) {
  return ({ fast_path: 'fast path', reconcile: 'reconcile', home_today: 'home today', manual: 'manual', backfill: 'backfill', extranet_export: 'eksport Extranet' }[path]) || path || '—';
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const start = new Date(todayIso());
  const target = new Date(dateStr);
  return Math.round((target - start) / 86400000);
}

function relativeCheckin(dateStr) {
  const days = daysUntil(dateStr);
  if (days === null) return '';
  if (days === 0) return 'przyjazd dzisiaj';
  if (days === 1) return 'przyjazd jutro';
  if (days > 1) return `przyjazd za ${days} dni`;
  return 'historia';
}

function bookingBucket(booking) {
  const today = todayIso();
  if (booking.booking_status === 'cancelled' || booking.status === 'cancelled') return 'cancelled';
  if (booking.checkin <= today && booking.checkout > today) return 'now';
  if (booking.checkin === today || booking.checkout === today) return 'today';
  if (booking.checkout < today || booking.booking_status === 'done') return 'history';
  return 'upcoming';
}

function needsSettlement(booking) {
  return booking.cleaning_status === 'done' && (booking.cleaning_payment_status || 'unpaid') !== 'paid';
}

export default function Reservations({ bookings, reload, user }) {
  const ctx = useOutletContext() || {};
  const finalUser = user || ctx.user || {};
  const finalBookings = bookings ?? ctx.bookings;
  const finalReload = reload || ctx.reload;
  const dashboard = ctx.dashboard;
  const safeUser = finalUser && finalUser.role ? finalUser : { role: 'guest' };
  const [form,setForm] = useState({guest_name:'', checkin:'', checkout:'', notes:'', guest_count: 2});
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState('current');
  const navigate = useNavigate();
  
  async function add(e){
  e.preventDefault(); 
  await api('/bookings',{method:'POST',body:JSON.stringify({ ...form, property_id: ctx.selectedApartmentId || null })});
  setForm({guest_name:'',checkin:'',checkout:'',notes:'',guest_count:2}); 
setShowForm(false); 
    await finalReload?.();
  }

  async function acknowledgeCleaning(e, cleaningJobId) {
    e.stopPropagation();
    if (!cleaningJobId) return;
    await api(`/cleaning-jobs/${cleaningJobId}/acknowledge`, {method: 'PATCH'});
    await finalReload?.();
  }
  
  const allBookings = finalBookings || [];
  function visibleForSearch(b) {
    const q = search.toLowerCase();
    return !q || [b.guest_name, b.external_id, b.checkin, b.checkout, b.notes, b.channel]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(q));
  }
  const filtered = allBookings
    .filter(b => {
      const bucket = bookingBucket(b);
      if (filterStatus === 'current') return bucket === 'now' || bucket === 'today' || bucket === 'upcoming';
      if (filterStatus === 'settlement') return needsSettlement(b);
      if (filterStatus === 'upcoming') return bucket === 'upcoming' || bucket === 'today';
      if (filterStatus === 'history') return bucket === 'history';
      if (filterStatus === 'cancelled') return bucket === 'cancelled';
      return true;
    })
    .filter(visibleForSearch)
    .sort((a, b) => {
      const order = { now: 0, today: 1, upcoming: 2, history: 3, cancelled: 4 };
      const bucketA = bookingBucket(a);
      const bucketB = bookingBucket(b);
      if (order[bucketA] !== order[bucketB]) return order[bucketA] - order[bucketB];
      if (bucketA === 'history') return String(b.checkout).localeCompare(String(a.checkout));
      return String(a.checkin).localeCompare(String(b.checkin));
    });

  const isStaff = safeUser.role === 'staff';
  const nowCount = allBookings.filter(b => bookingBucket(b) === 'now').length;
  const upcomingCount = allBookings.filter(b => ['today','upcoming'].includes(bookingBucket(b))).length;
  const historyCount = allBookings.filter(b => bookingBucket(b) === 'history').length;
  const settlementCount = allBookings.filter(needsSettlement).length;
  const nextPrep = dashboard?.next_guest_preparation;
  
  const filters = ['current','upcoming','today','settlement','history','cancelled'];
  const filterLabels = { current: 'Teraz', upcoming: 'Nadchodzące', today: 'Dzisiaj', settlement: 'Do rozliczenia', history: 'Historia', cancelled: 'Anulowane' };
  const filterColors = { current: '#2563eb', upcoming: '#16a34a', today: '#d97706', settlement: '#dc2626', history: '#64748b', cancelled: '#991b1b' };
  
  // Tooltips explain where the status comes from. They are deliberately
  // long-form so Monika / Maria can understand without checking Telegram.
  const TOOLTIPS = {
    lifecycle: {
      new: 'Nowa rezerwacja z Booking.com — gość nie potwierdził jeszcze lub Maria nie dostała sygnału o gotowości mieszkania.',
      awaiting_arrival: 'Maria potwierdziła na WhatsApp, że mieszkanie jest gotowe. Czekamy na przyjazd gościa.',
      in_house: 'Gość jest w mieszkaniu.',
      checkout_today: 'Gość wyjeżdża dziś. Maria powinna zaplanować sprzątanie.',
      completed: 'Pobyt zakończony, sprzątanie po wyjeździe zrobione.',
      cancelled: 'Rezerwacja anulowana.',
    },
    readiness: {
      ready: 'Mieszkanie gotowe na przyjazd. Komunikat zweryfikowany.',
      in_progress: 'Maria sprząta mieszkanie. Sprawdź szczegóły zadania w Sprzątanie.',
      planned: 'Sprzątanie zaplanowane na ten dzień. Maria jeszcze nie zaczęła.',
      missing: 'Brak potwierdzenia od Marii. Napisz do grupy WhatsApp lub zadzwoń.',
      not_required: 'Sprzątanie związane z tą rezerwacją nie dotyczy tej karty.',
    },
  };

  const statusBadge = (b) => {
    const lifecycleStyles = {
      new: { bg: '#f3e8ff', color: '#7c3aed', icon: '🆕' },
      awaiting_arrival: { bg: '#dcfce7', color: '#166534', icon: '🏠' },
      in_house: { bg: '#fef9c3', color: '#ca8a04', icon: '🏠' },
      checkout_today: { bg: '#ffedd5', color: '#d97706', icon: '📅' },
      completed: { bg: '#dcfce7', color: '#16a34a', icon: '✔' },
      cancelled: { bg: '#fee2e2', color: '#dc2626', icon: '✕' },
    };
    const readinessStyles = {
      ready: { bg: '#dcfce7', color: '#166534', icon: '✅' },
      in_progress: { bg: '#fef3c7', color: '#b45309', icon: '🧹' },
      planned: { bg: '#e5e7eb', color: '#4b5563', icon: '📅' },
      missing: { bg: '#fee2e2', color: '#991b1b', icon: '⚠️' },
      not_required: null,
    };
    const lc = lifecycleStyles[b.lifecycle_status] || { bg: '#f1f5f9', color: '#64748b', icon: '•' };
    const rd = readinessStyles[b.readiness_status];
    const lcTip = TOOLTIPS.lifecycle[b.lifecycle_status] || b.lifecycle_status;
    const rdTip = TOOLTIPS.readiness[b.readiness_status] || b.readiness_status;
    return (
      <span style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>
        <span
          title={lcTip}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: lc.bg, color: lc.color, borderRadius: '20px', padding: '3px 10px', fontSize: '12px', fontWeight: '600', cursor: 'help', borderBottom: '1px dotted currentColor' }}
        >
          {lc.icon} {b.lifecycle_label || b.lifecycle_status}
        </span>
        {rd && (
          <span
            title={rdTip}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: rd.bg, color: rd.color, borderRadius: '20px', padding: '3px 10px', fontSize: '12px', fontWeight: '600', cursor: 'help', borderBottom: '1px dotted currentColor' }}
          >
            {rd.icon} {b.readiness_label || b.readiness_status}
          </span>
        )}
      </span>
    );
  };
  
  return (
    <section>
      <PageHeader
        icon="📅"
        title="Rezerwacje"
        description="Oś czasu jednego apartamentu: obecny pobyt, najbliższy przyjazd, przygotowanie dla Marii i rozliczenia."
        helpKey="reservations"
        stats={[
          { label: 'Pobyt teraz', value: nowCount, tone: nowCount ? 'primary' : 'info' },
          { label: 'Nadchodzące', value: upcomingCount, tone: 'info' },
          { label: 'Historia', value: historyCount },
          { label: 'Do rozliczenia', value: settlementCount, tone: settlementCount ? 'danger' : 'success' },
        ]}
        actions={safeUser.role === 'admin' ? (
          <button
            className="primary"
            onClick={() => setShowForm(s => !s)}
            disabled={showForm}
          >
            {showForm ? 'Anuluj' : '+ Dodaj rezerwację'}
          </button>
        ) : null}
      />

      {safeUser.role==='admin' && showForm && (
        <form className="card form" onSubmit={add}>
          <div className="form-header">
            <h2>Dodaj rezerwację</h2>
            <button type="button" className="btn-close" onClick={()=>setShowForm(false)}>✕</button>
          </div>
          <input placeholder="Gość" value={form.guest_name} onChange={e=>setForm({...form,guest_name:e.target.value})}/>
          <input type="number" min="1" max="10" placeholder="Osób" value={form.guest_count} onChange={e=>setForm({...form,guest_count:Number(e.target.value)})}/>
          <input type="date" value={form.checkin} onChange={e=>setForm({...form,checkin:e.target.value})}/>
          <input type="date" value={form.checkout} onChange={e=>setForm({...form,checkout:e.target.value})}/>
          <input placeholder="Notatka" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/>
          <button>Dodaj i utwórz sprzątanie</button>
        </form>
      )}

      {!isStaff && (
        <div className="booking-filters">
          {filters.map(f => (
            <button key={f} className={`filter-pill ${filterStatus === f ? 'active' : ''}`}
              style={{ background: filterStatus === f ? filterColors[f] : '#f1f5f9', color: filterStatus === f ? 'white' : '#64748b' }}
              onClick={() => setFilterStatus(f)}>
              {filterLabels[f]}
            </button>
          ))}
        </div>
      )}

      {!isStaff && (
        <input className="search-bar" placeholder="🔍 Szukaj gościa, numeru rezerwacji, daty lub notatki..." value={search} onChange={e=>setSearch(e.target.value)}/>
      )}
      
      {nextPrep?.guest_count > 0 && (
        <div className="card reservation-prep-summary">
          <strong>Dla Marii przy najbliższym przyjeździe: {nextPrep.guest_count} osób</strong>
          <span>{nextPrep.label}</span>
        </div>
      )}

      <div className="booking-cards">
        {filtered.length === 0 && <div className="empty">Brak rezerwacji{search ? ' pasujących do wyszukiwania' : ''}.</div>}
        {filtered.map(b => (
          <div key={b.id || b.external_id} className={`booking-card booking-${bookingBucket(b)} ${needsSettlement(b) ? 'needs-settlement' : ''}`} onClick={() => navigate(`/rezerwacje/${b.external_id || b.id}`)}>
            <div className="booking-header">
              <strong>{isStaff ? `🔖 Rezerwacja ${reservationNumber(b.external_id, b.id)}` : `👤 ${b.guest_name}`}</strong>
              {statusBadge(b)}
              <span className="guest-count-chip">🛏️ {b.guest_count || 0} osób</span>
              <CleaningStatusChip 
                cleaning_job_id={b.cleaning_job_id} 
                cleaning_status={b.cleaning_status} 
                cleaning_scheduled_date={b.cleaning_scheduled_date}
              />
            </div>
            <div className="booking-dates">📅 {b.checkin} → {b.checkout} · {relativeCheckin(b.checkin)}</div>
            <div className="booking-import-line">
              <span>Import: {formatWarsawDateTime(b.imported_at)}</span>
              {b.import_lag_seconds !== null && b.import_lag_seconds !== undefined && (
                <span
                  className="import-lag-badge"
                  style={{ background: importLagTone(b.import_lag_seconds), color: 'white', borderRadius: '999px', padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}
                >
                  {formatLag(b.import_lag_seconds)}
                </span>
              )}
              <span className="muted">{importPathLabel(b.import_path)}</span>
            </div>
            <div className="booking-maria-note">Dla Marii: pościel i ręczniki dla {b.guest_count || 0} osób</div>
            {isStaff && b.cleaning_job_id && (
              <div className="booking-maria-actions" onClick={e => e.stopPropagation()}>
                {b.cleaning_acknowledged ? (
                  <span className="ack-badge">✓ Widziane</span>
                ) : (
                  <button className="secondary small" onClick={e => acknowledgeCleaning(e, b.cleaning_job_id)}>Widziałam</button>
                )}
              </div>
            )}
            {needsSettlement(b) && <div className="booking-settlement-note">Maria: niezapłacone {Number(b.cleaning_payment_amount || 100).toFixed(0)} PLN</div>}
            {b.notes && <div className="booking-note">💬 {b.notes}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}